import { useState, useRef, useEffect } from "react";
import { motion, useInView, MotionConfig } from "framer-motion";
import { staggerContainer, staggerItem } from "@/lib/motion";
import { i18n, type Lang } from "@/data/i18n";
import {
  Shield,
  Rocket,
  Crown,
  Users,
  Layers,
  Globe,
  Lock,
  ChevronDown,
  Menu,
  X,
  Mail,
  MapPin,
  ArrowRight,
  Languages,
} from "lucide-react";

const HERO_IMG = "https://agents-download.skywork.ai/image/rt/f1e8f17b107855289a5dab215fbd713d.jpg";
const LEGACY_IMG = "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-24/prod_agent_a17141e1-9bb9-4bf9-9b69-9f5da6b22392/succession_4_22cdb8234afb4c18b3810304c7fa8a88.jpeg";
const TEAM_IMG = "https://agents-download.skywork.ai/image/rt/2802d1152708f5006f5b6a7237459e2f.jpg";
const LOGO_IMG = "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-19/prod_agent_a17141e1-9bb9-4bf9-9b69-9f5da6b22392/rf-logo_a75ec8c2b0ca48f0abc4372c574c19f0.svg";

const SERIF = { fontFamily: "'Playfair Display', serif" };

// ─── Section wrapper ───
function Section({ id, children, className = "", dark = false }: { id?: string; children: React.ReactNode; className?: string; dark?: boolean }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section id={id} ref={ref} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={staggerContainer} className={`relative py-20 md:py-28 ${dark ? "bg-primary text-primary-foreground" : ""} ${className}`}>
      {children}
    </motion.section>
  );
}

// ─── Icon mapper ───
function Icon({ name, className = "w-6 h-6" }: { name: string; className?: string }) {
  const map: Record<string, React.ReactNode> = { shield: <Shield className={className} />, rocket: <Rocket className={className} />, crown: <Crown className={className} />, users: <Users className={className} />, layers: <Layers className={className} />, globe: <Globe className={className} />, lock: <Lock className={className} /> };
  return <>{map[name] || <Shield className={className} />}</>;
}

// ─── Navbar ───
function Navbar({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { const fn = () => setScrolled(window.scrollY > 60); window.addEventListener("scroll", fn); return () => window.removeEventListener("scroll", fn); }, []);
  const nav = (href: string) => { setMobileOpen(false); document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }); };
  const items = i18n.nav[lang];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/90 backdrop-blur-md shadow-sm border-b border-border/50" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 md:h-20">
        <a href="#hero" onClick={(e) => { e.preventDefault(); nav("#hero"); }} className="flex items-center gap-1">
          <img src={LOGO_IMG} alt="Regent Family Fund Logo" className="w-14 h-14 md:w-16 md:h-16" />
          <span className="text-xl md:text-2xl font-bold tracking-tight" style={SERIF}>
            <span className={scrolled ? "text-foreground" : "text-white"}>Regent</span>
            <span className="text-accent ml-1">Family Fund</span>
          </span>
        </a>
        <nav className="hidden lg:flex items-center gap-8">
          {items.map((item) => (
            <button key={item.href} onClick={() => nav(item.href)} className={`text-sm font-medium transition-colors hover:text-accent ${scrolled ? "text-foreground/70" : "text-white/80"}`}>{item.label}</button>
          ))}
          <button onClick={() => setLang(lang === "en" ? "zh" : "en")} className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border transition-colors ${scrolled ? "border-border text-foreground/70 hover:text-accent" : "border-white/30 text-white/80 hover:text-accent"}`}>
            <Languages className="w-4 h-4" />
            {lang === "en" ? "中文" : "EN"}
          </button>
        </nav>
        <div className="lg:hidden flex items-center gap-2">
          <button onClick={() => setLang(lang === "en" ? "zh" : "en")} className={`text-xs font-medium px-2 py-1 rounded border ${scrolled ? "border-border text-foreground/70" : "border-white/30 text-white/80"}`}>
            {lang === "en" ? "中文" : "EN"}
          </button>
          <button className="p-2" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className={`w-6 h-6 ${scrolled ? "text-foreground" : "text-white"}`} /> : <Menu className={`w-6 h-6 ${scrolled ? "text-foreground" : "text-white"}`} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="lg:hidden bg-background/95 backdrop-blur-md border-b border-border">
          <nav className="flex flex-col px-6 py-4 gap-3">
            {items.map((item) => (<button key={item.href} onClick={() => nav(item.href)} className="text-left text-sm font-medium text-foreground/80 hover:text-accent py-2">{item.label}</button>))}
          </nav>
        </motion.div>
      )}
    </header>
  );
}

// ─── Hero ───
function Hero({ lang }: { lang: Lang }) {
  const t = i18n.hero[lang];
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Financial cityscape" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/70 to-primary/90" />
      </div>
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 200, damping: 30, delay: 0.2 }}>
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-4">{t.badge}</p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6" style={SERIF}>{t.title}</h1>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-4">{t.subtitle}</p>
          <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto mb-10">{t.mission}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent text-accent-foreground font-semibold rounded-lg hover:opacity-90 transition-opacity">
              {t.cta1} <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 px-8 py-3.5 border border-white/30 text-white font-medium rounded-lg hover:bg-white/10 transition-colors">
              {t.cta2}
            </button>
          </div>
        </motion.div>
        <motion.div className="absolute bottom-10 left-1/2 -translate-x-1/2" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ChevronDown className="w-6 h-6 text-white/50" />
        </motion.div>
      </div>
    </section>
  );
}

// ─── About ───
function About({ lang }: { lang: Lang }) {
  const t = i18n.about[lang];
  const stats = i18n.stats[lang];
  return (
    <Section id="about">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div variants={staggerItem} className="text-center mb-16">
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">{t.tag}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" style={SERIF}>{t.title}</h2>
          <p className="text-muted-foreground max-w-3xl mx-auto text-base md:text-lg leading-relaxed">{t.intro}</p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div variants={staggerItem} className="space-y-6">
            <p className="text-foreground/80 leading-relaxed">{t.p1}</p>
            <p className="text-foreground/80 leading-relaxed">{t.p2}</p>
            <div className="grid grid-cols-2 gap-4 pt-4">
              {stats.map((s) => (
                <div key={s.label} className="p-4 rounded-xl bg-muted/50 border border-border/50">
                  <p className="text-accent font-bold text-lg" style={SERIF}>{s.value}</p>
                  <p className="text-muted-foreground text-sm mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div variants={staggerItem} className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img src={TEAM_IMG} alt="Professional team" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-accent text-accent-foreground p-6 rounded-xl shadow-lg">
              <p className="text-2xl font-bold" style={SERIF}>SPC</p>
              <p className="text-sm opacity-80">{lang === "en" ? "Segregated Portfolio" : "独立投资组合架构"}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

// ─── Structure ───
function Structure({ lang }: { lang: Lang }) {
  const t = i18n.structure[lang];
  const portfolios = i18n.portfolios[lang];
  const icons = ["shield", "rocket"];
  return (
    <Section id="structure" dark>
      <div className="max-w-7xl mx-auto px-6">
        <motion.div variants={staggerItem} className="text-center mb-16">
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">{t.tag}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" style={SERIF}>{t.title}</h2>
          <p className="text-primary-foreground/70 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">{t.intro}</p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8">
          {portfolios.map((p, idx) => (
            <motion.div key={p.name} variants={staggerItem} className="relative p-8 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center"><Icon name={icons[idx]} className="w-6 h-6 text-accent" /></div>
                <div><h3 className="text-xl font-bold" style={SERIF}>{p.name}</h3><p className="text-sm text-accent">{p.focus}</p></div>
              </div>
              <p className="text-primary-foreground/70 text-sm leading-relaxed mb-6">{p.description}</p>
              <div className="flex flex-wrap gap-2">
                {p.sectors.map((s) => (<span key={s} className="px-3 py-1 text-xs rounded-full bg-accent/10 text-accent border border-accent/20">{s}</span>))}
              </div>
              <div className="absolute top-6 right-6"><span className="text-xs font-medium px-3 py-1 rounded-full bg-accent/20 text-accent">{p.strategy}</span></div>
            </motion.div>
          ))}
        </div>
        <motion.div variants={staggerItem} className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.spcAdvantages.map((item) => (
            <div key={item.title} className="text-center p-6 rounded-xl border border-white/10">
              <h4 className="font-semibold text-accent mb-2">{item.title}</h4>
              <p className="text-sm text-primary-foreground/60">{item.desc}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}

// ─── Philosophy ───
function PhilosophySection({ lang }: { lang: Lang }) {
  const t = i18n.philosophy[lang];
  return (
    <Section id="philosophy">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div variants={staggerItem} className="text-center mb-16">
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">{t.tag}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" style={SERIF}>{t.title}</h2>
          <p className="text-muted-foreground max-w-3xl mx-auto text-base md:text-lg">{t.intro}</p>
        </motion.div>
        <div className="grid md:grid-cols-5 gap-6">
          {t.items.map((item, i) => (
            <motion.div key={item.title} variants={staggerItem} className="text-center p-6 rounded-2xl bg-muted/30 border border-border/50 hover:border-accent/30 transition-colors">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-accent font-bold text-sm" style={SERIF}>{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-semibold text-base mb-2">{item.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <motion.div variants={staggerItem} className="mt-20">
          <h3 className="text-2xl md:text-3xl font-bold text-center mb-12" style={SERIF}>{t.processTitle}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.process.map((step) => (
              <motion.div key={step.step} variants={staggerItem} className="flex gap-4 p-6 rounded-xl bg-card border border-border/50 hover:shadow-md transition-shadow">
                <span className="text-3xl font-bold text-accent/30" style={SERIF}>{step.step}</span>
                <div><h4 className="font-semibold mb-1">{step.title}</h4><p className="text-muted-foreground text-sm">{step.description}</p></div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

// ─── Advantages ───
function AdvantagesSection({ lang }: { lang: Lang }) {
  const t = i18n.advantages[lang];
  return (
    <Section id="advantages" className="bg-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div variants={staggerItem} className="text-center mb-16">
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">{t.tag}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" style={SERIF}>{t.title}</h2>
          <p className="text-muted-foreground max-w-3xl mx-auto text-base md:text-lg">{t.intro}</p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.items.map((adv) => (
            <motion.div key={adv.title} variants={staggerItem} className="p-8 rounded-2xl bg-card border border-border/50 hover:border-accent/30 hover:shadow-lg transition-all group">
              <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-5 group-hover:bg-accent/20 transition-colors">
                <Icon name={adv.icon} className="w-7 h-7 text-accent" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{adv.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{adv.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ─── Team ───
function TeamSection({ lang }: { lang: Lang }) {
  const t = i18n.team[lang];
  const boardMembers = t.members.filter((m) => m.group === "board");
  const icMembers = t.members.filter((m) => m.group === "ic");

  return (
    <Section id="team">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div variants={staggerItem} className="text-center mb-16">
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">{t.tag}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" style={SERIF}>{t.title}</h2>
          <p className="text-muted-foreground max-w-3xl mx-auto text-base md:text-lg">{t.intro}</p>
        </motion.div>

        {/* Board of Directors */}
        <motion.div variants={staggerItem} className="mb-6">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-2" style={SERIF}>
            <div className="w-1 h-6 bg-accent rounded-full" />
            {t.boardTitle}
          </h3>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {boardMembers.map((member) => (
            <motion.div key={member.name} variants={staggerItem} className="p-8 rounded-2xl bg-card border border-border/50 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-5 mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-2xl font-bold text-primary" style={SERIF}>{member.name[0]}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold" style={SERIF}>{member.name}</h3>
                  <p className="text-accent text-sm font-medium">{member.role}</p>
                  <p className="text-muted-foreground text-sm mt-1">{member.education}</p>
                </div>
              </div>
              <p className="text-foreground/70 text-sm leading-relaxed mb-5">{member.background}</p>
              <div className="space-y-2">
                {member.expertise.map((exp) => (
                  <div key={exp} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />{exp}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Investment Committee */}
        <motion.div variants={staggerItem} className="mb-6">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-2" style={SERIF}>
            <div className="w-1 h-6 bg-accent rounded-full" />
            {t.icTitle}
          </h3>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8">
          {icMembers.map((member) => (
            <motion.div key={member.name} variants={staggerItem} className="p-8 rounded-2xl bg-card border border-border/50 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-5 mb-6">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                  <span className="text-2xl font-bold text-accent" style={SERIF}>{member.name[0]}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold" style={SERIF}>{member.name}</h3>
                  <p className="text-accent text-sm font-medium">{member.role}</p>
                  <p className="text-muted-foreground text-sm mt-1">{member.education}</p>
                </div>
              </div>
              <p className="text-foreground/70 text-sm leading-relaxed mb-5">{member.background}</p>
              <div className="space-y-2">
                {member.expertise.map((exp) => (
                  <div key={exp} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />{exp}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ─── Wealth Succession ───
function WealthSection({ lang }: { lang: Lang }) {
  const t = i18n.wealth[lang];
  return (
    <Section dark className="overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div variants={staggerItem}>
            <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">{t.tag}</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={SERIF}>{t.title}</h2>
            <p className="text-primary-foreground/70 leading-relaxed mb-6">{t.description}</p>
            <ul className="space-y-4">
              {t.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-primary-foreground/80 text-sm">
                  <div className="w-2 h-2 rounded-full bg-accent mt-1.5 shrink-0" />{item}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div variants={staggerItem} className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src={LEGACY_IMG} alt="Family legacy" className="w-full h-full object-cover" />
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

// ─── Contact ───
function Contact({ lang }: { lang: Lang }) {
  const t = i18n.contact[lang];
  return (
    <Section id="contact" className="bg-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div variants={staggerItem} className="text-center mb-16">
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">{t.tag}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" style={SERIF}>{t.title}</h2>
          <p className="text-muted-foreground max-w-3xl mx-auto text-base md:text-lg">{t.intro}</p>
        </motion.div>
        <motion.div variants={staggerItem} className="max-w-2xl mx-auto">
          <div className="p-8 md:p-12 rounded-2xl bg-card border border-border/50 shadow-sm">
            <div className="space-y-6">
              {t.addresses.map((addr, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-accent" /></div>
                  <div><h4 className="font-semibold mb-1">{addr.title}</h4><p className="text-muted-foreground text-sm">{addr.value}</p></div>
                </div>
              ))}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-accent" /></div>
                <div>
                  <h4 className="font-semibold mb-1">{t.business.title}</h4>
                  <p className="text-muted-foreground text-sm">{t.business.value}</p>
                  <a href={`mailto:${t.business.email}`} className="text-accent font-medium text-sm hover:underline mt-1 inline-block">{t.business.email}</a>
                </div>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-border/50">
              <p className="text-muted-foreground text-xs leading-relaxed">{t.disclaimer}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

// ─── Footer ───
function Footer({ lang }: { lang: Lang }) {
  const t = i18n.footer[lang];
  return (
    <footer className="bg-primary text-primary-foreground py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <div className="flex items-center gap-1 mb-2">
              <img src={LOGO_IMG} alt="Regent Family Fund Logo" className="w-14 h-14" />
              <p className="text-xl font-bold" style={SERIF}>Regent <span className="text-accent">Family Fund</span> SPC</p>
            </div>
            <p className="text-primary-foreground/50 text-sm mt-1">{t.tagline}</p>
          </div>
          <div className="text-center md:text-right">
            <p className="text-primary-foreground/50 text-xs leading-relaxed max-w-md">{t.disclaimer}</p>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-primary-foreground/40 text-xs">© {new Date().getFullYear()} Regent Family Fund SPC. All rights reserved.</p>
          <div className="flex gap-6 text-primary-foreground/40 text-xs">
            {t.links.map((link) => (<span key={link} className="hover:text-accent cursor-pointer transition-colors">{link}</span>))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ───
const Index = () => {
  const [lang, setLang] = useState<Lang>("en");
  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-background">
        <Navbar lang={lang} setLang={setLang} />
        <Hero lang={lang} />
        <About lang={lang} />
        <Structure lang={lang} />
        <PhilosophySection lang={lang} />
        <AdvantagesSection lang={lang} />
        <TeamSection lang={lang} />
        <WealthSection lang={lang} />
        <Contact lang={lang} />
        <Footer lang={lang} />
      </div>
    </MotionConfig>
  );
};

export default Index;
